import { ColumnsType } from 'antd/es/table';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import {
  ContractItemPriceRaise,
  ContractItemPriceRaiseStatus,
  getPriceRaisePercent,
  getNewPriceInvoicePeriod,
  getPriceRaiseProposedUnitPrice,
  isPriceRaiseAboveMaxPercent,
  priceRaiseStatusBadgeColorMap,
  priceRaiseStatusLabelMap,
  useGetContractItemPriceRaisesQuery,
} from 'entities/Contract';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { UserPermissions } from 'entities/User';
import { useAcceptPriceRaise } from 'features/AcceptPriceRaise';
import { useRejectPriceRaise } from 'features/RejectPriceRaise';
import { SendPriceRaiseNotification, useSendPriceRaiseNotification } from 'features/SendPriceRaiseNotification';
import { FC, memo, MouseEvent, useCallback, useMemo, useState } from 'react';
import { ReactComponent as EmailIcon } from 'shared/assets/icons/EmailIcon.svg';
import { ReactComponent as CancelIcon } from 'shared/assets/icons/CancelIcon.svg';
import { ReactComponent as CompletedStepIcon } from 'shared/assets/icons/CompletedStepIcon.svg';
import { InputNumber } from 'shared/ui/InputNumber';
import { Modal } from 'shared/ui/Modal';
import { ServerTable } from 'shared/ui/ServerTable';
import { RowBadge } from 'shared/ui/ServerTable/RowBadge';
import { RowSideBadge } from 'shared/ui/ServerTable/RowSideBadge';
import { ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { getClientDateFormat, getClientDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { usePriceRaiseHistoryModal } from '../utils/hooks/usePriceRaiseHistoryModal';

export const PriceRaiseHistoryModal: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);
  const currencySymbol = useGetCurrencySymbol();
  const maxPriceRaisePercent = useAppSelector(getGlobalSettings)?.maxPriceRaisePercent ?? 10;
  const { isOpened, contractId, closePriceRaiseHistoryModal } = usePriceRaiseHistoryModal();
  const [draftPrices, setDraftPrices] = useState<Record<string, number>>({});
  const { openAcceptPriceRaise, acceptPriceRaiseModal } = useAcceptPriceRaise();
  const { openRejectPriceRaise, rejectPriceRaiseModal } = useRejectPriceRaise();
  const isForbiddenManagePriceRaise = useCheckPermission(UserPermissions.MANAGE_PRICE_RAISE);
  const {
    open: openSendNotification,
    close: closeSendNotification,
    send: sendNotification,
    isOpened: isSendNotificationOpened,
    isLoading: isSendNotificationLoading,
  } = useSendPriceRaiseNotification();

  const stopRowClick = useCallback((event: MouseEvent): void => {
    event.stopPropagation();
  }, []);

  const getRowPrice = useCallback(
    (record: ContractItemPriceRaise): number => {
      return draftPrices[record.contractItemPriceRaiseId] ?? record.newPrice;
    },
    [draftPrices],
  );

  const isEnteredPriceAboveMax = useCallback(
    (record: ContractItemPriceRaise): boolean => {
      return isPriceRaiseAboveMaxPercent(record.oldPrice, getRowPrice(record), maxPriceRaisePercent);
    },
    [getRowPrice, maxPriceRaisePercent],
  );

  const changeDraftPrice = useCallback((contractItemPriceRaiseId: string, value: Nullable<number>): void => {
    if (value == null) {
      return;
    }

    setDraftPrices((prev) => ({ ...prev, [contractItemPriceRaiseId]: value }));
  }, []);

  const openAcceptModal = useCallback(
    (record: ContractItemPriceRaise): void => {
      openAcceptPriceRaise({
        contract: record.contract,
        newPrice: getRowPrice(record),
        currentPrice: record.oldPrice,
        contractItem: record.contractItem,
        priceRaiseDate: record.priceRaiseDate,
        contractItemPriceRaiseId: record.contractItemPriceRaiseId,
      });
    },
    [getRowPrice, openAcceptPriceRaise],
  );

  const openRejectModal = useCallback(
    (record: ContractItemPriceRaise): void => {
      openRejectPriceRaise({
        contract: record.contract,
        newPrice: getRowPrice(record),
        oldPrice: record.oldPrice,
        contractItem: record.contractItem,
        contractItemPriceRaiseId: record.contractItemPriceRaiseId,
      });
    },
    [getRowPrice, openRejectPriceRaise],
  );

  const rowActions: Array<ServerTableRowActions<ContractItemPriceRaise>> = useMemo(
    () => [
      {
        name: 'reject',
        theme: 'danger',
        label: t('Reject'),
        description: t('Reject'),
        onClick: openRejectModal,
        isDisabled: (item) => isForbiddenManagePriceRaise || isEnteredPriceAboveMax(item),
        isHidden: (item) =>
          item.status !== ContractItemPriceRaiseStatus.ACCEPTED && item.status !== ContractItemPriceRaiseStatus.REVIEW_AGAIN,
      },
      {
        name: 'accept',
        label: t('Accept'),
        description: t('Accept'),
        onClick: openAcceptModal,
        isDisabled: (item) => isForbiddenManagePriceRaise || isEnteredPriceAboveMax(item),
        isHidden: (item) => {
          if (item.status === ContractItemPriceRaiseStatus.REJECTED) {
            return false;
          }

          return item.status !== ContractItemPriceRaiseStatus.REVIEW_AGAIN || Boolean(item.notificationSentAt);
        },
      },
      {
        name: 'sendNotification',
        icon: <EmailIcon className="[&>path]:stroke-primary" />,
        theme: 'clear',
        label: t('Send notification'),
        description: t('Send notification'),
        onClick: (item) => {
          openSendNotification(item.contractItemPriceRaiseId);
        },
        isDisabled: () => isForbiddenManagePriceRaise,
        isHidden: (item) => item.status !== ContractItemPriceRaiseStatus.ACCEPTED,
      },
    ],
    [isEnteredPriceAboveMax, isForbiddenManagePriceRaise, openAcceptModal, openRejectModal, openSendNotification, t],
  );

  const columns = useMemo<ColumnsType<ContractItemPriceRaise>>(
    () => [
      {
        title: t('Size / Unit'),
        key: 'box',
        render: (_, record) => (
          <>
            <div>
              {record.contractItem?.box?.square} {getSizeCodeTypeString(record.contractItem?.box?.sizeCode?.sizeCodeType)}
            </div>
            <div className="font-normal text-sm">{record.contractItem?.box?.name}</div>
            <RowBadge color={priceRaiseStatusBadgeColorMap[record.status]} title={t(priceRaiseStatusLabelMap[record.status])} />
            {record.contract.noticeLeaveDate && (
              <RowSideBadge
                color="error"
                toolTipColor="white"
                toolTipFontColor="primary"
                title={`${t('Planned move-out date')}: ${getClientDateFormat(record.contract.noticeLeaveDate)}\n${t(
                  'Move-out notice date',
                )}: ${getClientDateFormat(record.contract.noticeGivenDate)}`}
              />
            )}
          </>
        ),
      },
      {
        title: t('Old price'),
        key: 'oldPrice',
        render: (_, record) => (
          <div>
            {record.oldPrice} {currencySymbol}
          </div>
        ),
      },
      {
        title: t('New price'),
        key: 'newPrice',
        render: (_, record) => {
          const proposedPrice = getPriceRaiseProposedUnitPrice(record.contract, record.contractItem?.box);
          const enteredPrice = getRowPrice(record);
          const percent = getPriceRaisePercent(record.oldPrice, enteredPrice);
          const percentLabel = `${percent > 0 ? '+' : ''}${percent}%`;
          const boxPricePercent = getPriceRaisePercent(record.oldPrice, proposedPrice);
          const boxPricePercentLabel = `${boxPricePercent > 0 ? '+' : ''}${boxPricePercent}%`;
          const isPriceLocked =
            record.status === ContractItemPriceRaiseStatus.REJECTED
              ? false
              : record.status !== ContractItemPriceRaiseStatus.REVIEW_AGAIN || Boolean(record.notificationSentAt);

          return (
            <div onClick={stopRowClick}>
              <div className="text-sm font-semibold">
                {t('Current unit price')}: {proposedPrice} {currencySymbol} ({boxPricePercentLabel})
              </div>
              <div className="w-40 mt-1">
                <InputNumber
                  bordered
                  size="small"
                  min={0.01}
                  value={enteredPrice}
                  status={isEnteredPriceAboveMax(record) ? 'error' : undefined}
                  addonAfter={currencySymbol}
                  isDisabled={isPriceLocked}
                  onChange={(value) => {
                    changeDraftPrice(record.contractItemPriceRaiseId, value);
                  }}
                />
              </div>
              {isEnteredPriceAboveMax(record) && (
                <div className="text-error text-sm font-normal mt-1 max-w-[220px]">
                  {t('The entered value is higher than the maximum allowed percentage of {{percent}}%', {
                    percent: maxPriceRaisePercent,
                  })}
                </div>
              )}
              <div className="text-sm font-normal mt-1">({percentLabel})</div>
            </div>
          );
        },
      },
      {
        title: t('Processed date'),
        key: 'processedAt',
        render: (_, record) => (record.createdAt ? getClientDateFormat(record.createdAt) : null),
      },
      {
        title: t('Notification sent date'),
        key: 'notificationSentAt',
        render: (_, record) => (record.notificationSentAt ? getClientDateWithMinuteTimeFormat(record.notificationSentAt) : null),
      },
      {
        title: t('Review again date'),
        key: 'nextReviewDate',
        render: (_, record) =>
          record.nextReviewDate ? <span className="text-error font-semibold">{getClientDateFormat(record.nextReviewDate)}</span> : null,
      },
      {
        title: t('Price raise date'),
        key: 'priceRaiseDate',
        render: (_, record) => {
          const period = getNewPriceInvoicePeriod(record.priceRaiseDate, record.contract);

          if (!record.priceRaiseDate) {
            return null;
          }

          return (
            <div className="text-xs">
              <div className="text-success font-semibold mb-2">{`${t('Invoicing date')}: ${getClientDateFormat(
                record.priceRaiseDate,
              )}`}</div>
              {period && (
                <div>
                  {`${t('New price invoice period')}: ${getClientDateFormat(period.dateFrom)} - ${getClientDateFormat(period.dateTo)}`}
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [changeDraftPrice, currencySymbol, getRowPrice, isEnteredPriceAboveMax, maxPriceRaisePercent, stopRowClick, t],
  );

  return (
    <>
      <Modal width={1200} isOpen={isOpened} onClose={closePriceRaiseHistoryModal} zIndex={1100}>
        {contractId && (
          <ServerTable
            fetchData={useGetContractItemPriceRaisesQuery}
            columns={columns}
            rowKey="contractItemPriceRaiseId"
            title={t('Price raise history')}
            defaultFilters={{ contractId }}
            rowActions={rowActions}
            rowActionsLimit={1}
            rowClassName={(record) => (record.contract.noticeLeaveDate ? 'bg-warnLight' : '')}
          />
        )}
      </Modal>
      {acceptPriceRaiseModal}
      {rejectPriceRaiseModal}
      <SendPriceRaiseNotification
        isOpened={isSendNotificationOpened}
        isLoading={isSendNotificationLoading}
        onConfirm={sendNotification}
        onCancel={closeSendNotification}
      />
    </>
  );
});
